import { ArrowRight, Loader2 } from "lucide-react";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { Chip } from "@/components/chip";
import {
  DataTable,
  StateText,
  TableCell,
  TableFacts,
  TableHead,
  TableHeadCell,
  TableItem,
  TableRow,
} from "@/components/data-table";
import { ToolIcon } from "@/components/tool-icon";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  type ConnectorTool,
  type ToolDefinition,
  type ToolParameter,
  useSetToolEnabled,
  useToolDefinition,
} from "@/lib/api";
import { apiErrorCode, apiErrorMessage } from "@/lib/api-errors";
import { formatNumber } from "@/lib/format";
import { CALLS_PATH } from "@/lib/issues";
import { isAdminRole, isDeveloper } from "@/lib/roles";
import { factLine } from "@/lib/table-facts";
import { isServed } from "@/lib/tool-groups";

import { AudienceFact } from "./audience-badge";
import { toolOpenerId } from "./tool-row";
import {
  defaultText,
  personalState,
  type Serving,
  switchErrorMessage,
  unavailableReasonText,
} from "./utils";

/** A tool's calls on Statistieken: the Oproepen part, filtered on it (`?tool=`). */
export function toolCallsPath(name: string): string {
  return `${CALLS_PATH}?${new URLSearchParams({ tool: name }).toString()}`;
}

interface ToolDialogProps {
  /** The tool on show, as the overview has it now; null when none is open. */
  tool: ConnectorTool | null;
  onClose: () => void;
  /** The title of the tool's group, in the dashboard's language. */
  groupTitle: string;
  /** The viewer's dashboard role: the admin roles see "Voor" and Statistieken, a developer the switch for everyone. */
  role: string | undefined;
  /** Whether the developers' switches for everyone could be read (`tool_switches_known`). */
  toolSwitchesKnown: boolean;
  /** What the connector serves, for why it does not serve the tool. */
  serving: Serving;
}

/**
 * A tool's dialog, for everyone, opened from its row (`ToolDetailsPanel`).
 * The dialog itself takes the focus when it opens, so no tooltip pops up,
 * and gives it back to the tool's title in the table when it closes.
 */
export function ToolDialog({
  tool,
  onClose,
  groupTitle,
  role,
  toolSwitchesKnown,
  serving,
}: ToolDialogProps) {
  const content = useRef<HTMLDivElement>(null);
  // The tool whose title gets the focus back, kept while the dialog closes.
  const [last, setLast] = useState<string | null>(null);
  if (tool !== null && tool.name !== last) {
    setLast(tool.name);
  }
  return (
    <Dialog open={tool !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        ref={content}
        className="max-h-[90dvh] overflow-y-auto focus:outline-none sm:max-w-2xl"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          content.current?.focus();
        }}
        onCloseAutoFocus={(event) => {
          // The dialog has no trigger of its own: go back to the tool's title in the table.
          const opener = last === null ? null : document.getElementById(toolOpenerId(last));
          if (opener) {
            event.preventDefault();
            opener.focus();
          }
        }}
      >
        {tool && (
          // Afresh per tool, so a failed switch never carries over to another tool.
          <ToolDetailsPanel
            key={tool.name}
            tool={tool}
            groupTitle={groupTitle}
            role={role}
            toolSwitchesKnown={toolSwitchesKnown}
            serving={serving}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface ToolDetailsPanelProps {
  tool: ConnectorTool;
  groupTitle: string;
  role: string | undefined;
  toolSwitchesKnown: boolean;
  serving: Serving;
}

/**
 * The body of a tool's dialog. On top its icon, title (the dialog's title),
 * technical name, "Tijdelijk uit" or "Binnenkort", and summary (its
 * description, so it renders inside a `<Dialog>`). Then its facts: read or
 * write, its group, for the admin roles who it is for (`AudienceFact`),
 * its size for Claude in tokens and, for the admin roles, a link to its
 * calls on Statistieken.
 * For a developer the switch that pauses it for everyone ("Uit voor
 * iedereen"). Then what Claude reads about it, verbatim in English, and its
 * parameters (`ToolDefinitionParts`). Only a tool the connector serves has a
 * definition, so none is fetched for the others: a tool coming soon says
 * Claude cannot use it yet, and a tool the connector does not serve here
 * (the row's "Niet beschikbaar", `personalState`) says why. A paused tool is
 * still served, and shows its definition.
 */
export function ToolDetailsPanel({
  tool,
  groupTitle,
  role,
  toolSwitchesKnown,
  serving,
}: ToolDetailsPanelProps) {
  const { t } = useTranslation();
  const served = isServed(tool);
  const personal = personalState(tool, serving);
  const notServed = personal.kind === "comingSoon" ? null : personal.unavailable;
  const hasDefinition = served && notServed === null;
  const definition = useToolDefinition(hasDefinition ? tool.name : null);
  const admin = isAdminRole(role);
  return (
    <div className="space-y-5">
      <DialogHeader className="space-y-2">
        <div className="flex items-start gap-2.5">
          <ToolIcon name={tool.name} className="text-primary mt-0.5 size-5 shrink-0" />
          <div className="min-w-0 space-y-1">
            <DialogTitle className="leading-tight">{tool.title}</DialogTitle>
            <code className="text-muted block font-mono text-xs wrap-anywhere">{tool.name}</code>
          </div>
        </div>
        {(tool.paused || !served) && (
          <div className="flex flex-wrap gap-1.5">
            {tool.paused && <Chip>{t("tools.paused")}</Chip>}
            {!served && <Chip tone="soon">{t("tools.comingSoon")}</Chip>}
          </div>
        )}
        <DialogDescription className="text-foreground max-w-prose">
          {tool.summary}
        </DialogDescription>
      </DialogHeader>

      <dl className="space-y-2 text-sm" data-section="facts">
        <Fact label={t("tools.details.access")} name="access">
          <Chip tone={tool.access}>{t(`tools.access.${tool.access}`)}</Chip>
        </Fact>
        <Fact label={t("tools.details.group")} name="group">
          {groupTitle}
        </Fact>
        {admin && tool.audience !== null && (
          <Fact label={t("tools.details.audience")} name="audience">
            <AudienceFact audience={tool.audience} />
          </Fact>
        )}
        {hasDefinition && (
          <Fact label={t("tools.details.size")} name="size">
            {definition.data ? (
              t("tools.details.sizeValue", { tokens: formatNumber(definition.data.size_tokens) })
            ) : (
              // Still loading, or failed: the definition's part says why.
              <span className="text-muted">{definition.isError ? "–" : "…"}</span>
            )}
          </Fact>
        )}
        {admin && served && (
          <Fact label={t("tools.details.usage")} name="usage">
            {/* Inline, so on a phone the arrow follows the last word rather than the cell's edge. */}
            <Link to={toolCallsPath(tool.name)} className="text-accent font-medium hover:underline">
              {t("tools.details.usageLink")}
              <ArrowRight aria-hidden="true" className="ml-1 inline size-4 align-[-0.2em]" />
            </Link>
          </Fact>
        )}
      </dl>

      {isDeveloper(role) && tool.switchable === true && (
        <KillSwitch tool={tool} switchesKnown={toolSwitchesKnown} />
      )}

      {!served ? (
        <p className="text-muted text-sm">{t("tools.details.comingSoon")}</p>
      ) : notServed !== null ? (
        <p className="text-muted text-sm" data-section="not-served">
          {t("tools.details.notServedBecause", { reason: unavailableReasonText(notServed) })}
        </p>
      ) : (
        <ToolDefinitionParts query={definition} onRetry={() => void definition.refetch()} />
      )}
    </div>
  );
}

interface FactProps {
  label: string;
  /** Its `data-fact`, for tests. */
  name: string;
  children: ReactNode;
}

/** A fact about the tool: its label in a fixed column, and its value. */
function Fact({ label, name, children }: FactProps) {
  return (
    <div className="flex items-baseline gap-3">
      <dt className="text-muted w-24 shrink-0">{label}</dt>
      <dd className="min-w-0" data-fact={name}>
        {children}
      </dd>
    </div>
  );
}

interface KillSwitchProps {
  tool: ConnectorTool;
  /** False when the developers' switches could not be read: the switch is then disabled. */
  switchesKnown: boolean;
}

/**
 * A developer's switch that pauses the tool for everyone: "Uit voor
 * iedereen", on while the tool is paused. No optimistic update: while the
 * request runs the switch is disabled at the server's value, and a failure is
 * said under it until the tool's switch changes.
 */
function KillSwitch({ tool, switchesKnown }: KillSwitchProps) {
  const { t } = useTranslation();
  const ids = useId();
  const setEnabled = useSetToolEnabled();
  const { reset } = setEnabled;
  useEffect(() => {
    reset();
  }, [tool.enabled, reset]);
  const paused = tool.enabled === false;
  const error = setEnabled.isError
    ? switchErrorMessage(setEnabled.error, setEnabled.variables?.enabled ?? paused)
    : null;
  return (
    <div className="rounded-md border px-3 py-2.5" data-section="kill-switch">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <label htmlFor={`${ids}-switch`} className="text-foreground text-sm font-semibold">
            {t("tools.details.killSwitch")}
          </label>
          <p id={`${ids}-hint`} className="text-muted text-xs">
            {t("tools.details.killSwitchHint")}
          </p>
        </div>
        <Switch
          id={`${ids}-switch`}
          checked={paused}
          onCheckedChange={(off) => setEnabled.mutate({ name: tool.name, enabled: !off })}
          disabled={!switchesKnown || setEnabled.isPending}
          aria-describedby={`${ids}-hint`}
        />
      </div>
      {error && (
        <p role="alert" className="text-destructive mt-2 text-xs">
          {error}
        </p>
      )}
    </div>
  );
}

interface ToolDefinitionPartsProps {
  query: {
    data: ToolDefinition | undefined;
    isError: boolean;
    error: unknown;
  };
  onRetry: () => void;
}

/**
 * What Claude reads about a tool: its description verbatim, in English, in a
 * tinted box, and its parameters in a dense table (`ParameterTable`); while
 * the definition loads a spinner, and when it fails why, with Opnieuw
 * proberen. A `tool_not_found` means the connector does not serve the tool
 * after all (a paused tool it never registered here, or the overview is
 * behind): asking again would only answer the same, so it says Claude does
 * not get the tool here.
 */
function ToolDefinitionParts({ query, onRetry }: ToolDefinitionPartsProps) {
  const { t } = useTranslation();
  const ids = useId();
  if (query.isError && apiErrorCode(query.error) === "tool_not_found") {
    return (
      <p className="text-muted text-sm" data-section="not-served">
        {t("tools.details.notServed")}
      </p>
    );
  }
  if (query.isError) {
    return (
      <p className="text-muted text-sm" role="alert">
        {apiErrorMessage(query.error, "loadToolDefinition")}{" "}
        <button type="button" onClick={onRetry} className="text-accent font-medium hover:underline">
          {t("common.tryAgain")}
        </button>
      </p>
    );
  }
  const definition = query.data;
  if (!definition) {
    return (
      <p className="text-muted flex items-center gap-2 text-sm">
        <Loader2 aria-hidden="true" className="size-4 animate-spin" />
        {t("tools.details.loading")}
      </p>
    );
  }
  return (
    <>
      <section aria-labelledby={`${ids}-reads`} data-section="description" className="space-y-2">
        <h3 id={`${ids}-reads`} className="text-foreground text-sm font-semibold">
          {t("tools.details.claudeReads")}
        </h3>
        {definition.description ? (
          <div
            lang="en"
            className="bg-muted/10 text-foreground rounded-md border p-3 text-xs leading-relaxed wrap-anywhere whitespace-pre-wrap"
          >
            {definition.description}
          </div>
        ) : (
          <p className="text-muted text-sm">{t("tools.details.noDescription")}</p>
        )}
      </section>
      <section
        aria-labelledby={`${ids}-parameters`}
        data-section="parameters"
        className="space-y-2"
      >
        <h3 id={`${ids}-parameters`} className="text-foreground text-sm font-semibold">
          {t("tools.details.parameters")}
          <span className="text-muted ml-1 font-normal tabular-nums">
            {formatNumber(definition.parameters.length)}
          </span>
        </h3>
        {definition.parameters.length > 0 ? (
          <ParameterTable parameters={definition.parameters} />
        ) : (
          <p className="text-muted text-sm">{t("tools.details.noParameters")}</p>
        )}
      </section>
    </>
  );
}

/**
 * A tool's parameters as Claude reads them, one row each in the schema's
 * order: its name in mono with its description under it (in English, as in
 * the schema), its type, whether it is required, and its default as JSON. In
 * the table's phone layout the type goes under the description on a line of
 * its own (it may hold commas: "one of draft, posted"), then whether it is
 * required and its default. A type or a default breaks between words where it
 * can, so its column is never narrower than its longest word.
 */
function ParameterTable({ parameters }: { parameters: ToolParameter[] }) {
  const { t } = useTranslation();
  return (
    <DataTable label={t("tools.details.parameters")}>
      <TableHead>
        <TableHeadCell>{t("tools.details.parameter.name")}</TableHeadCell>
        <TableHeadCell narrow="hide">{t("tools.details.parameter.type")}</TableHeadCell>
        <TableHeadCell narrow="hide">{t("tools.details.parameter.required")}</TableHeadCell>
        <TableHeadCell narrow="hide">{t("tools.details.parameter.default")}</TableHeadCell>
      </TableHead>
      <tbody>
        {parameters.map((parameter) => {
          const fallback = defaultText(parameter.default);
          return (
            <TableRow key={parameter.name}>
              <TableCell>
                <TableItem
                  title={
                    <code className="font-mono text-xs wrap-anywhere hyphens-none">
                      {parameter.name}
                    </code>
                  }
                  summary={parameter.description && <span lang="en">{parameter.description}</span>}
                  facts={
                    <>
                      <TableFacts>{parameter.type}</TableFacts>
                      {(parameter.required || fallback !== null) && (
                        <TableFacts>
                          {factLine([
                            parameter.required && t("tools.details.requiredFact"),
                            fallback !== null &&
                              t("tools.details.defaultFact", { value: fallback }),
                          ])}
                        </TableFacts>
                      )}
                    </>
                  }
                />
              </TableCell>
              <TableCell narrow="hide">
                <span className="text-muted block text-xs wrap-break-word">{parameter.type}</span>
              </TableCell>
              <TableCell narrow="hide">
                {parameter.required ? (
                  <span className="text-xs font-medium">{t("tools.details.yes")}</span>
                ) : (
                  <StateText>{t("tools.details.no")}</StateText>
                )}
              </TableCell>
              <TableCell narrow="hide">
                {fallback === null ? (
                  <StateText>–</StateText>
                ) : (
                  <code className="block font-mono text-xs wrap-break-word">{fallback}</code>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </tbody>
    </DataTable>
  );
}
